"use client";

/**
 * Audio file ingest: drag & drop or picker. The File is handed off as a
 * handle — bytes are only read downstream by the transcription pipeline.
 */
import { useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { cn } from "@/lib/utils";

const ACCEPT = "audio/*,.mp3,.m4a,.wav,.webm,.ogg,.flac";

/** The five-bar waveform mark shared with the transcribing card. */
const BAR_HEIGHTS = [8, 16, 11, 18, 7];

interface AudioDropzoneProps {
  onFileSelected: (file: File) => void;
}

export function AudioDropzone({ onFileSelected }: AudioDropzoneProps) {
  const t = useTranslations("Flow.dropzone");
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);

  function handleFiles(files: FileList | null) {
    const file = files?.[0];
    if (file) onFileSelected(file);
  }

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={t("ariaLabel")}
      className={cn(
        "w-full max-w-[620px] cursor-pointer rounded-2xl border border-dashed bg-card p-[46px_32px] text-center shadow-elev transition-[border-color,transform,background-color] duration-200 hover:-translate-y-0.5 hover:border-input",
        dragActive
          ? "scale-[1.012] border-primary bg-tint"
          : "border-input/70",
      )}
      onClick={() => inputRef.current?.click()}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") inputRef.current?.click();
      }}
      onDragOver={(e) => {
        e.preventDefault();
        setDragActive(true);
      }}
      onDragLeave={() => setDragActive(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragActive(false);
        handleFiles(e.dataTransfer.files);
      }}
    >
      <div className="mx-auto mb-[18px] flex h-[52px] w-[52px] items-end justify-center gap-[3px] rounded-full bg-tint pb-[17px]">
        {BAR_HEIGHTS.map((height, i) => (
          <span
            key={i}
            className="w-1 rounded-sm bg-primary"
            style={{ height }}
          />
        ))}
      </div>
      <p className="mb-1 font-serif text-2xl font-medium tracking-[-0.3px]">
        {t("title")}
      </p>
      <p className="mb-4 text-sm text-muted-foreground">
        {t.rich("browse", {
          u: (chunks) => (
            <span className="underline underline-offset-[3px]">{chunks}</span>
          ),
        })}
      </p>
      <p className="font-mono text-[11.5px] text-muted-foreground">
        {t("formats")}
      </p>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        className="hidden"
        onChange={(e) => {
          handleFiles(e.target.files);
          e.target.value = "";
        }}
      />
    </div>
  );
}
